/**
 * Modals shared by the Access-device surfaces: the tenant-wide Secure Access →
 * Devices page and the per-user Access Devices tab (VSNP-1163). One copy of
 * the revoke / detail dialogs, so an admin sees the same thing whichever door
 * they came in through. Pure helpers live in utils/accessDevice.ts.
 */

import { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import accessService, { type AccessDevice } from '../../services/access.service';
import {
  describeSubjects,
  deviceStatusLabel,
  deviceSubjects,
  tokenBindingDisplay,
  fmtDate,
  postureFacts,
  postureSource,
} from '../../utils/accessDevice';

/**
 * Revoke requires a reason (server-enforced), and fans the revocation to the
 * fleet. `tenantId` lets a SuperAdmin act from a cross-tenant page (the
 * VPN-user detail) where the tenant selector may be on All Tenants.
 */
export const RevokeDeviceModal = ({
  device,
  tenantId,
  onClose,
  onDone,
}: {
  device: AccessDevice | null;
  tenantId?: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (device) setReason('');
  }, [device]);

  const submit = async () => {
    if (!device || !reason.trim()) return;
    try {
      setSubmitting(true);
      const result = await accessService.revokeDevice(device.id, reason.trim(), tenantId);
      const fanout = result.revocationFanout;
      toast.success(
        'Device revoked',
        fanout
          ? `Pushed to ${fanout.delivered}/${fanout.targeted} gateway(s) — live sessions drop now`
          : device.device_id,
      );
      await onDone();
    } catch (err) {
      toast.error('Revoke failed', err instanceof Error ? err.message : 'Could not revoke the device');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={!!device}
      onClose={onClose}
      title="Revoke device"
      size="md"
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="btn-danger" onClick={submit} disabled={submitting || !reason.trim()}>
            {submitting ? 'Revoking…' : 'Revoke device'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Revoking <strong>{device?.device_name || device?.device_id}</strong> refuses its certificate and pushes the
          revocation to every gateway, dropping any live sessions within seconds.
        </p>
        <div>
          <label htmlFor="revoke-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason<span className="text-danger-600 ml-0.5">*</span>
          </label>
          <input
            id="revoke-reason"
            className="input-base w-full"
            placeholder="Lost device / offboarding / compromised"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500">Recorded on the device and in the audit log.</p>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Reassign a device to a new owner (VSNP-1298, design 23 §9.4 decisions B/C).
 *
 * Tenant administrators only — the caller gates the action, the server enforces
 * it. Deliberately worded as a destructive operation, because it is: the
 * current certificate is revoked fleet-wide and the device goes back to
 * pending approval, so the new owner must enrol and an admin must approve
 * before it works again. An admin who only wants to SHARE a device adds extra
 * subjects here instead of moving it.
 */
export const ReassignDeviceModal = ({
  device,
  tenantId,
  onClose,
  onDone,
}: {
  device: AccessDevice | null;
  tenantId?: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [extra, setExtra] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (device) {
      setEmail('');
      setExtra('');
    }
  }, [device]);

  const current = deviceSubjects(device);

  const submit = async () => {
    if (!device || !email.trim()) return;
    try {
      setSubmitting(true);
      // One address per line, blank lines ignored. Everything typed here is an
      // email — the `sub` and `username` kinds exist on the wire but are not
      // things an administrator can usefully type, so they are not offered.
      const subjects = extra
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((value) => ({ kind: 'email' as const, value }));

      const result = await accessService.reassignDevice(
        device.id,
        { userEmail: email.trim(), ...(subjects.length ? { subjects } : {}) },
        tenantId,
      );
      const fanout = result.revocationFanout;
      toast.success(
        'Device reassigned',
        result.certificateRevoked
          ? `Certificate revoked${fanout ? ` on ${fanout.delivered}/${fanout.targeted} gateway(s)` : ''} — ${device.device_id} is awaiting approval`
          : `${device.device_id} is awaiting approval`,
      );
      await onDone();
    } catch (err) {
      toast.error('Reassign failed', err instanceof Error ? err.message : 'Could not reassign the device');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={!!device}
      onClose={onClose}
      title="Reassign device"
      size="md"
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="btn-danger" onClick={submit} disabled={submitting || !email.trim()}>
            {submitting ? 'Reassigning…' : 'Revoke & reassign'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-800">
          <strong>The current certificate will be revoked.</strong> Reassigning{' '}
          <strong>{device?.device_name || device?.device_id}</strong> pushes the revocation to every gateway — any live
          session drops within seconds — and returns the device to <em>pending approval</em>. The new owner must enrol
          again, and an administrator must approve it, before it can connect.
        </div>

        <div>
          <div className="text-xs text-gray-500">Current owner</div>
          <div className="text-sm text-gray-900">{device?.user_email || '— (unassigned)'}</div>
          {current.length > 0 && (
            <div className="mt-1 text-xs text-gray-500" title={describeSubjects(current)}>
              {current.length} subject{current.length !== 1 ? 's' : ''}: {current.map((s) => s.value).join(', ')}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="reassign-email" className="block text-sm font-medium text-gray-700 mb-1">
            New owner<span className="text-danger-600 ml-0.5">*</span>
          </label>
          <input
            id="reassign-email"
            type="email"
            className="input-base w-full"
            placeholder="new.owner@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500">
            The new owner replaces the subject list — the previous owner is removed, not kept alongside.
          </p>
        </div>

        <div>
          <label htmlFor="reassign-extra" className="block text-sm font-medium text-gray-700 mb-1">
            Additional subjects <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            id="reassign-extra"
            className="input-base w-full font-mono text-xs"
            rows={3}
            placeholder={'shared.user@example.com\nkiosk.user@example.com'}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            One address per line, for a shared or kiosk device. Anyone listed may enrol and present this device — leave
            empty unless you mean it.
          </p>
        </div>
      </div>
    </Modal>
  );
};

/** Read-only detail: identity, verified posture facts, and certificate history. */
export const DeviceDetailModal = ({ device, onClose }: { device: AccessDevice | null; onClose: () => void }) => {
  const facts = postureFacts(device?.posture);
  const source = postureSource(device?.posture);
  return (
    <Modal isOpen={!!device} onClose={onClose} title={device?.device_name || device?.device_id || 'Device'} size="lg">
      {device && (
        <div className="space-y-5 text-sm">
          <section className="grid grid-cols-2 gap-x-6 gap-y-2">
            <Field label="Device ID (CN)" value={device.device_id} mono />
            <Field label="Status" value={deviceStatusLabel(device.status)} />
            <Field label="Owner" value={device.user_email} />
            <Field label="Platform" value={`${device.platform}${device.os_version ? ` · ${device.os_version}` : ''}`} />
            <Field label="Client version" value={device.client_version} />
            <Field label="Enrolled" value={fmtDate(device.enrolled_at)} />
            <Field label="Last seen" value={fmtDate(device.last_seen)} />
            {/* The binding outcome the gateway recorded the last time this
                device opened a session (WP8B). Shown with its own timestamp:
                a device that has not connected since enforcement shipped has
                no result, which is a different claim from "unbound". */}
            <Field
              label="Token binding"
              value={
                device.last_token_binding
                  ? `${tokenBindingDisplay(device.last_token_binding).label} · ${fmtDate(device.last_token_binding_at)}`
                  : null
              }
            />
            {device.status === 'revoked' && (
              <>
                <Field label="Revoked" value={`${fmtDate(device.revoked_at)}${device.revoked_by ? ` by ${device.revoked_by}` : ''}`} />
                <Field label="Reason" value={device.revocation_reason} />
              </>
            )}
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Subjects
              <span className="ml-2 normal-case font-normal text-gray-400">
                identities allowed to enrol and present this device
              </span>
            </h3>
            {deviceSubjects(device).length === 0 ? (
              <p className="text-gray-500">
                None recorded. Nobody may enrol this device id until an administrator reassigns it —{' '}
                <span className="font-medium">an unowned device is not self-claimable.</span>
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {deviceSubjects(device).map((s) => (
                  <span
                    key={`${s.kind}:${s.value}`}
                    className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
                    title={s.addedBy ? `added by ${s.addedBy}${s.addedAt ? ` on ${fmtDate(s.addedAt)}` : ''}` : undefined}
                  >
                    <span className="text-gray-400">{s.kind}</span>
                    <span className="font-mono">{s.value}</span>
                  </span>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Verified posture
              {source && <span className="ml-2 normal-case font-normal text-gray-400">source: {source}</span>}
            </h3>
            {facts.length === 0 ? (
              <p className="text-gray-500">
                No verified posture yet. Facts appear once the device reports posture and the gateway verifies it against
                the registered key. <span className="font-medium">Unknown never satisfies a posture requirement.</span>
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {facts.map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 rounded border border-teal-200 bg-teal-50 px-2 py-1 text-xs text-teal-800">
                    <span className="font-medium">{k}</span>
                    <span className="text-teal-500">=</span>
                    <span className="font-mono">{v}</span>
                  </span>
                ))}
                {device.posture_checked_at && (
                  <span className="text-xs text-gray-400 self-center">checked {fmtDate(device.posture_checked_at)}</span>
                )}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Certificate</h3>
            {device.certificate_serial_number ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <Field label="Serial" value={device.certificate_serial_number} mono />
                <Field label="Thumbprint (SHA-256)" value={device.certificate_thumbprint_s256} mono />
                <Field label="Issued" value={fmtDate(device.certificate_issued_at)} />
                <Field label="Expires" value={fmtDate(device.certificate_expires_at)} />
              </div>
            ) : (
              <p className="text-gray-500">No certificate issued.</p>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-gray-900 break-all ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</div>
  </div>
);
